import { useCallback, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface TabItem {
  readonly value: string;
  readonly label: ReactNode;
  readonly badge?: number;
  readonly disabled?: boolean;
}

export interface TabsProps {
  items: readonly TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'aria-label': string;
}

/**
 * Tabs following the WAI-ARIA manual-activation pattern: arrow keys move focus
 * between tabs and Enter or Space selects. Manual rather than automatic
 * activation because selecting a tab here can trigger a fetch, and we do not
 * want arrowing past a tab to issue a request.
 */
export function Tabs({ items, value, onChange, className, ...rest }: TabsProps) {
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const focusTab = useCallback((index: number) => {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]:not(:disabled)',
    );
    if (!tabs || tabs.length === 0) return;
    const wrapped = ((index % tabs.length) + tabs.length) % tabs.length;
    tabs[wrapped]?.focus();
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          focusTab(index + 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          focusTab(index - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusTab(0);
          break;
        case 'End':
          event.preventDefault();
          focusTab(items.length - 1);
          break;
      }
    },
    [focusTab, items.length],
  );

  const enabled = items.filter((i) => !i.disabled);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={rest['aria-label']}
      className={cn('flex items-center gap-1 border-b border-line -mb-px', className)}
    >
      {items.map((item) => {
        const selected = item.value === value;
        const enabledIndex = enabled.findIndex((i) => i.value === item.value);

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`${baseId}-tab-${item.value}`}
            aria-selected={selected}
            aria-controls={`${baseId}-panel-${item.value}`}
            // Only the selected tab is in the tab order; arrow keys move
            // within the tablist. This is the roving tabindex pattern.
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => onKeyDown(e, enabledIndex)}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              selected
                ? 'border-brand text-brand-text'
                : 'border-transparent text-muted hover:text-body',
            )}
          >
            {item.label}
            {item.badge && item.badge > 0 ? (
              <span className="rounded-full bg-neutral-bg text-neutral-fg text-[10px] font-bold px-1.5 py-0.5 tabular-nums">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  value: string;
  active: string;
  /** Must match the `aria-label` passed to the sibling Tabs. */
  tabsId?: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ value, active, children, className }: TabPanelProps) {
  if (value !== active) return null;
  return (
    <div role="tabpanel" tabIndex={0} className={cn('focus:outline-none', className)}>
      {children}
    </div>
  );
}
